/**
 * Password and device security through Supabase Auth.
 *
 * - A forgotten password: the reset email links back to /reset-password with
 *   a reset session. With two-factor set up, Supabase requires a session that
 *   entered a code (aal2) to change the password, so the reset link alone
 *   cannot take over an account.
 * - Changing the password signs out every other device (Supabase revokes
 *   their refresh tokens).
 * - "Sign out other devices" does the same without a password change.
 */

import { supabase } from './supabaseClient';

/** Our minimum for a new password; set the Supabase project minimum to match. */
export const PASSWORD_MIN_LENGTH = 12;

const RESET_PASSWORD_PATH = '/reset-password';

interface NewPasswordInput {
  password: string;
  /** Checked by Supabase when "require current password" is on (a reset session is exempt). */
  currentPassword?: string;
}

/** Email a reset link. Succeeds whether or not the address has an account. */
export async function sendPasswordResetEmail(email: string): Promise<void> {
  const { error } = await supabase.auth.resetPasswordForEmail(email, {
    redirectTo: `${window.location.origin}${RESET_PASSWORD_PATH}`,
  });
  if (error) throw error;
}

export async function updatePassword({
  password,
  currentPassword,
}: NewPasswordInput): Promise<void> {
  const { error } = await supabase.auth.updateUser({
    password,
    ...(currentPassword ? { current_password: currentPassword } : {}),
  });
  if (error) throw error;
}

/** End every session except this browser's (each device notices within an hour). */
export async function signOutOtherDevices(): Promise<void> {
  const { error } = await supabase.auth.signOut({ scope: 'others' });
  if (error) throw error;
}

/** Why a new password can't be used yet, or null when it can be sent. */
export function newPasswordProblem(password: string, confirmation: string): string | null {
  if (password.length < PASSWORD_MIN_LENGTH) {
    return `Use at least ${PASSWORD_MIN_LENGTH} characters.`;
  }
  if (password !== confirmation) {
    return "The passwords don't match.";
  }
  return null;
}

/**
 * The error Supabase appends to an auth email link it refused (expired or
 * already used): in the hash for the implicit flow, in the query otherwise.
 */
export function readAuthLinkError(location: Pick<Location, 'hash' | 'search'>): string | null {
  for (const part of [location.hash.replace(/^#/, ''), location.search.replace(/^\?/, '')]) {
    const params = new URLSearchParams(part);
    const description = params.get('error_description') ?? params.get('error');
    if (description) return description;
  }
  return null;
}
